import { Horse, Player } from '../tmmodel';

export enum GameType {
  Training = 'training',
  Free = 'free',
  TournamentBronze = 'tournamentbronze',
  TournamentSilver = 'tournamentsilver',
  TournamentGold = 'tournamentgold',
}

export enum GameStatus {
  Waiting = 'waiting',
  Running = 'running',
  Finished = 'finished',
  Cancelled = 'cancelled',
}

/** determines who can find and join the game: private = only via invitation link; public = visible in game list. */
export type GameVisibility = 'public' | 'private';

/** determines who can send chat messages: playersonly = only players can send; public = players and spectators can send. */
export type ChatVisibility = 'playersonly' | 'public';

/** accepts legacy DB values 'freebronze'|'freesilver'|'freegold' for backward compatibility. */
export const isFreeGameType = (gameType: GameType): boolean => {
  const s = String(gameType);
  // TODO: remove legacy values (freebronze, freesilver, freegold) once DB migration is done
  return gameType === GameType.Free || s === 'freebronze' || s === 'freesilver' || s === 'freegold';
};

export const isTournamentGameType = (gameType: GameType): boolean => {
  return (
    gameType === GameType.TournamentBronze ||
    gameType === GameType.TournamentSilver ||
    gameType === GameType.TournamentGold
  );
};

// check if game type is allowed with basic subscription
export const isGameTypeAllowedBasic = (gameType: GameType): boolean => {
  // only training mode is allowed without a premium subscription
  return gameType === GameType.Training;
};

// used by getgames
export interface GameOverview {
  gameId: string;
  timeCreated: string; // timestamp when this game was created (ISO 8601: YYYY-MM-DDTHH:mm:ss.sssZ)
  name: string;
  gameType: GameType;
  horseamount: number;
  players: Player[];
  horses: Horse[];
  /** determines who can find and join the game: public = visible in game list; private = only via invitation link. */
  visibility: GameVisibility;
  /** if true and visibility is public, spectators can watch. */
  allowSpectators: boolean;
  /** determines who can send chat messages: public = players and spectators can send; playersonly = only players can send. */
  chatVisibility: ChatVisibility;
  /** Game lifecycle: waiting (lobby), running, or finished. Enables UI to show Join only when joinable. */
  status?: GameStatus;
}

/** Filters for GET /games/history (all optional). */
export interface GameHistoryFilters {
  gameType?: GameType;
  dateFrom?: string; // ISO 8601
  dateTo?: string;
  playerId?: string; // userId
  status?: GameStatus; // default: completed (Finished)
}

/** One game in the history list (no snapshotId). */
export interface GameHistoryEntry {
  gameId: string;
  timeCreated: string;
  gameType: GameType;
  status: GameStatus;
  name?: string;
  players: Player[];
}

export enum EventType {
  // State machine states
  GETGAMES = 'GETGAMES',
  NEWGAME = 'NEWGAME',
  RESTOREGAME = 'RESTOREGAME',
  JOINGAME = 'JOINGAME',
  JOINGAMEOPP = 'JOINGAMEOPP',
  REJOINGAME = 'REJOINGAME',
  STARTGAME = 'STARTGAME',
  STARTROUND = 'STARTROUND',
  MERGECARDS = 'MERGECARDS',
  MERGECARDSOPP = 'MERGECARDSOPP',
  STARTTURN = 'STARTTURN',
  ADDBONUSCARDS = 'ADDBONUSCARDS',
  ADDBONUSCARDSOPP = 'ADDBONUSCARDSOPP',
  SKIPPLAYER = 'SKIPPLAYER',
  SELECTCARDDICE = 'SELECTCARDDICE',
  SELECTMOVE = 'SELECTMOVE',
  SELECTMOVEOPP = 'SELECTMOVEOPP',
  SELECTDICES = 'SELECTDICES',
  SELECTDICESOPP = 'SELECTDICESOPP',
  ENDTURN = 'ENDTURN',
  ENDROUND = 'ENDROUND',
  ENDGAME = 'ENDGAME',
  GETTOURNAMENTS = 'GETTOURNAMENTS',
  JOINTOURNAMENT = 'JOINTOURNAMENT',
  NEWMESSAGE = 'NEWMESSAGE',
  SENDMESSAGE = 'SENDMESSAGE',
  LEAVEGAME = 'LEAVEGAME',
  LEAVEGAMEOPP = 'LEAVEGAMEOPP',
  // Custom events
  TM_GAMECREATED = 'TM_GAMECREATED',
  TM_REJOINGAME = 'TM_REJOINGAME',
  // Fallback for unknown states
  UNKNOWN = 'UNKNOWN',
}

export interface EventLogEntry {
  senderId: string; // userId or "SERVER" for system messages
  timestamp: string; // timestamp when this message was sent (ISO 8601: YYYY-MM-DDTHH:mm:ss.sssZ)
  eventType: EventType; // enumeration with state machine states and custom events
  payload: any; // object with various properties (depends on eventType)
}

export interface GameState {
  gameId: string;
  timeCreated: string; // timestamp when this game was created (ISO 8601: YYYY-MM-DDTHH:mm:ss.sssZ)
  gameType: GameType;
  boardId: number;
  status: GameStatus;
  eventLog: EventLogEntry[];
  lastRemovedCards: number[];
  state: any;
  statePrev?: any;
  statePrevPrev?: any;
  /** determines who can find and join the game: public = visible in game list; private = only via invitation link. */
  visibility: GameVisibility;
  /** if true and visibility is public, spectators can watch. */
  allowSpectators: boolean;
  /** determines who can send chat messages: public = players and spectators can send; playersonly = only players can send. */
  chatVisibility: ChatVisibility;
}

/** UI context captured when user opens feedback/report (DOM path, position, component, element summary). */
export interface UiContext {
  domPath?: string;
  position?: { top: number; left: number; width: number; height: number };
  reactComponent?: string;
  htmlElement?: string;
}

/** Snapshot type for reporting snapshots only (no auto-creation when games end). */
export type SnapshotType = 'Debug' | 'Manual';

export interface GameSnapshot {
  snapshotId: string;
  timestamp: string;
  userId: string;
  message: string;
  userAgent: string;
  gameState: GameState;
  /** Optional contact (email or other) for developer follow-up. */
  contactEmail?: string;
  /** Optional UI context (element that triggered report). */
  uiContext?: UiContext;
  /** Optional game ID this snapshot refers to (reporting snapshots only). */
  gameId?: string;
  /** Type of snapshot: Debug (from report) or Manual (created manually). */
  snapshotType?: SnapshotType;
}

export interface GameSnapshotRequest {
  message?: string;
  userAgent?: string;
  gameState: GameState;
  contactEmail?: string;
  uiContext?: UiContext;
}
export interface GameCreateSnapshotRequest {
  message?: string;
  userAgent?: string;
  contactEmail?: string;
  uiContext?: UiContext;
}

export type SpawnSource = 'snapshot' | 'game';

export interface SpawnGameRequest {
  source: SpawnSource;
  sourceId: string; // snapshotId or gameId
  eventIndex: number;
  controlPlayerIndex: number; // which player the user takes control of (0-based)
}

export interface SpawnGameResponse {
  gameId: string;
}

/** Built-in debug template category (server-defined in data/commonSnapshots.ts). */
export type CommonSnapshotCategory = 'new-game' | 'template';

export type SnapshotListItemSource = 'common' | 'server';

/** Sortable fields for GET /games/snapshots list. */
export type SnapshotListSortBy = 'timestamp' | 'message' | 'snapshotType' | 'userId';

/** Query params for paginated snapshot list (all optional). */
export interface SnapshotListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: SnapshotListSortBy;
  sortDir?: 'asc' | 'desc';
  /** When set, return only built-in templates (`common`) or persisted snapshots (`server`). */
  source?: SnapshotListItemSource;
}

/** One row in the unified snapshots table (built-in templates + persisted reports). */
export interface SnapshotListItem {
  source: SnapshotListItemSource;
  id: string;
  snapshotType: string;
  message: string;
  userId: string;
  timestamp: string | null;
  eventCount: number | null;
  /** Server snapshot only */
  snapshotId?: string;
  hasStatePrev?: boolean;
  hasStatePrevPrev?: boolean;
  /** Common template only */
  commonCategory?: CommonSnapshotCategory;
  commonGameName?: string;
  commonPlayerCount?: number;
  commonHorseCount?: number;
}

/** Paginated snapshot list response (valid records only). */
export interface SnapshotListResponse {
  items: SnapshotListItem[];
  total: number;
  page: number;
  pageSize: number;
}
